import React from 'react';
import {
    View,
    Text,
    StyleSheet,
    StatusBar,
    TouchableOpacity,
    Image,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Feather } from '@expo/vector-icons';
import { COLORS, APP_INFO } from '../config';
import { useAuth } from '../context/AuthContext';

export default function KartuMemberScreen({ navigation }) {
    const insets = useSafeAreaInsets();
    const { user } = useAuth();

    const formatNumber = (num) => {
        return num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.') || '0';
    };

    const memberCode = user?.kode_member || 'MING-' + String(user?.id).padStart(4, '0');

    // Generate QR code URL using free web API
    const qrData = JSON.stringify({
        type: 'member',
        id: user?.id,
        kode: memberCode,
        nama: user?.nama,
    });
    const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(memberCode)}`;

    return (
        <View style={styles.container}>
            <StatusBar barStyle="light-content" backgroundColor={COLORS.primary} />

            {/* Header */}
            <View style={[styles.header, { paddingTop: insets.top + 12 }]}>
                <TouchableOpacity
                    style={styles.backBtn}
                    onPress={() => navigation.goBack()}
                >
                    <Feather name="arrow-left" size={24} color="#fff" />
                </TouchableOpacity>
                <Text style={styles.headerTitle}>Kartu Member</Text>
                <View style={{ width: 40 }} />
            </View>

            {/* Member Card */}
            <View style={styles.cardContainer}>
                <View style={styles.memberCard}>
                    {/* Card Header */}
                    <View style={styles.cardHeader}>
                        <View style={styles.logoContainer}>
                            <Text style={styles.logoText}>MING</Text>
                        </View>
                        <View style={styles.cardTitleContainer}>
                            <Text style={styles.cardTitle}>{APP_INFO.fullName}</Text>
                            <Text style={styles.cardSubtitle}>Member Card</Text>
                        </View>
                    </View>

                    {/* QR Code Section */}
                    <View style={styles.idSection}>
                        <View style={styles.qrContainer}>
                            <Image
                                source={{ uri: qrCodeUrl }}
                                style={styles.qrImage}
                                resizeMode="contain"
                            />
                        </View>
                        <Text style={styles.memberIdValue}>{memberCode}</Text>
                        <Text style={styles.scanText}>Scan QR untuk verifikasi member</Text>
                    </View>

                    {/* Member Info */}
                    <View style={styles.memberInfo}>
                        <View style={styles.infoRow}>
                            <Text style={styles.infoLabel}>Nama</Text>
                            <Text style={styles.infoValue}>{user?.nama || '-'}</Text>
                        </View>
                        <View style={styles.infoDivider} />
                        <View style={styles.infoRow}>
                            <Text style={styles.infoLabel}>No. HP</Text>
                            <Text style={styles.infoValue}>{user?.telepon || '-'}</Text>
                        </View>
                    </View>

                    {/* Card Footer */}
                    <View style={styles.cardFooter}>
                        <View style={styles.statItem}>
                            <Text style={styles.statValue}>{formatNumber(user?.saldo)}</Text>
                            <Text style={styles.statLabel}>Saldo MG</Text>
                        </View>
                        <View style={styles.statDivider} />
                        <View style={styles.statItem}>
                            <Text style={styles.statValue}>{formatNumber(user?.poin)}</Text>
                            <Text style={styles.statLabel}>Poin</Text>
                        </View>
                    </View>
                </View>
            </View>

            {/* Instructions */}
            <View style={styles.instructions}>
                <Feather name="info" size={16} color={COLORS.textMuted} />
                <Text style={styles.instructionText}>
                    Tunjukkan kartu ini ke kasir untuk transaksi tanpa membawa kartu fisik
                </Text>
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: COLORS.bgDark,
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: COLORS.primary,
        paddingHorizontal: 16,
        paddingBottom: 16,
    },
    backBtn: {
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: 'rgba(255,255,255,0.2)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    headerTitle: {
        fontSize: 18,
        fontWeight: '700',
        color: '#fff',
    },
    cardContainer: {
        padding: 20,
        paddingTop: 30,
    },
    memberCard: {
        backgroundColor: '#fff',
        borderRadius: 20,
        overflow: 'hidden',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 10 },
        shadowOpacity: 0.15,
        shadowRadius: 20,
        elevation: 8,
    },
    cardHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: COLORS.primary,
        padding: 20,
        gap: 12,
    },
    logoContainer: {
        width: 50,
        height: 50,
        borderRadius: 12,
        backgroundColor: '#fff',
        justifyContent: 'center',
        alignItems: 'center',
    },
    logoText: {
        fontSize: 14,
        fontWeight: '900',
        color: COLORS.primary,
    },
    cardTitleContainer: {
        flex: 1,
    },
    cardTitle: {
        fontSize: 18,
        fontWeight: '700',
        color: '#fff',
    },
    cardSubtitle: {
        fontSize: 12,
        color: 'rgba(255,255,255,0.8)',
        marginTop: 2,
    },
    idSection: {
        alignItems: 'center',
        paddingVertical: 24,
        backgroundColor: '#fff',
    },
    qrContainer: {
        padding: 12,
        backgroundColor: '#fff',
        borderRadius: 16,
        borderWidth: 2,
        borderColor: COLORS.border,
        marginBottom: 12,
    },
    qrImage: {
        width: 180,
        height: 180,
    },
    memberIdValue: {
        fontSize: 24,
        fontWeight: '700',
        color: COLORS.primary,
        letterSpacing: 2,
    },
    scanText: {
        marginTop: 12,
        fontSize: 12,
        color: COLORS.textMuted,
    },
    memberInfo: {
        paddingHorizontal: 20,
        paddingBottom: 20,
    },
    infoRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingVertical: 12,
    },
    infoLabel: {
        fontSize: 13,
        color: COLORS.textMuted,
    },
    infoValue: {
        fontSize: 14,
        fontWeight: '600',
        color: COLORS.textPrimary,
    },
    infoDivider: {
        height: 1,
        backgroundColor: COLORS.border,
    },
    cardFooter: {
        flexDirection: 'row',
        borderTopWidth: 1,
        borderTopColor: COLORS.border,
        backgroundColor: COLORS.bgDark,
    },
    statItem: {
        flex: 1,
        alignItems: 'center',
        paddingVertical: 16,
    },
    statDivider: {
        width: 1,
        backgroundColor: COLORS.border,
    },
    statValue: {
        fontSize: 18,
        fontWeight: '700',
        color: COLORS.primary,
    },
    statLabel: {
        fontSize: 11,
        color: COLORS.textMuted,
        marginTop: 4,
    },
    instructions: {
        flexDirection: 'row',
        alignItems: 'center',
        marginHorizontal: 20,
        padding: 16,
        backgroundColor: COLORS.bgCard,
        borderRadius: 12,
        gap: 10,
    },
    instructionText: {
        flex: 1,
        fontSize: 12,
        color: COLORS.textMuted,
        lineHeight: 18,
    },
});
